import {
  HStack,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useContextTypeSettingsOrganization } from "../dashboard-layout";
import { stringMapSettingsOrganizationTab } from "../../../strings";
import Team from "./team";
import BexioSettings from "./bexio-settings";
import GeneralSettings from "./general-settings";
import ApprovalRules from "./approval-rules";
import { useEffect, useMemo } from "react";
import { showSettings } from "../../../utils";
import { routeSettingsOrganization } from "../../routes";
import { useApprovalEnabled } from "../../../hooks/approvals/useApprovalEnabled";
import { AccountingSystem } from "../../../API";

const settingsOrganizationTab = [
  "allgemein",
  "team",
  "freigaben",
  "bexio",
] as const;

export type SettingsOrganizationTab = (typeof settingsOrganizationTab)[number];

const SettingsOrganizationLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, selectedOrganization, bookkeepings } =
    useContextTypeSettingsOrganization();
  const isApprovalEnabled = useApprovalEnabled(selectedOrganization?.id);

  const hasBexio = bookkeepings?.some(
    (b) => b.accountingSystem === AccountingSystem.bexio
  );

  // Filter out "freigaben" when approval toggle is off, "bexio" when no bookkeeping uses bexio
  const visibleTabs = useMemo(
    () =>
      settingsOrganizationTab.filter(
        (t) =>
          (t !== "freigaben" || isApprovalEnabled) &&
          (t !== "bexio" || hasBexio)
      ),
    [isApprovalEnabled, hasBexio]
  );

  // Determine the index of the active tab based on the current route
  const activeTabIndex = visibleTabs.findIndex((t) =>
    location.pathname.includes(t)
  );

  const handleTabsChange = (index: number) => {
    navigate(`${routeSettingsOrganization}/${visibleTabs[index]}`);
  };

  useEffect(() => {
    // Redirect to the first tab if the current path doesn't match any tab or is the root route
    if (
      activeTabIndex === -1 ||
      location.pathname === routeSettingsOrganization
    ) {
      navigate(`${routeSettingsOrganization}/${visibleTabs[0]}`);
    }
  }, [activeTabIndex, location.pathname, navigate, visibleTabs]);

  if (
    user &&
    selectedOrganization &&
    !showSettings(user, selectedOrganization)
  ) {
    return <Navigate to="/" />;
  }

  return (
    <VStack
      width="full"
      height="full"
      alignItems="start"
      spacing="24px"
      py="24px"
    >
      <HStack>
        <Text fontSize={24} fontWeight="semibold" color="gray.900">
          Einstellungen
        </Text>
        {selectedOrganization ? (
          <Text fontSize={24} fontWeight="semibold" color="gray.900">
            {selectedOrganization.name}
          </Text>
        ) : (
          <Skeleton height="36px" width="120px" />
        )}
      </HStack>
      <Tabs
        width="full"
        height="full"
        variant="soft-rounded"
        colorScheme="primary"
        isLazy
        index={activeTabIndex}
        onChange={handleTabsChange}
      >
        <TabList>
          {visibleTabs.map((t) => (
            <Tab key={t} borderRadius={6}>
              {stringMapSettingsOrganizationTab.get(t)}{" "}
            </Tab>
          ))}
        </TabList>
        <TabPanels height="full">
          {visibleTabs.map((t) => (
            <TabPanel key={t} height="full">
              {t === "allgemein" ? (
                <GeneralSettings />
              ) : t === "team" ? (
                <Team />
              ) : t === "freigaben" ? (
                <ApprovalRules />
              ) : t === "bexio" ? (
                <BexioSettings />
              ) : (
                <></>
              )}
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>
    </VStack>
  );
};

export default SettingsOrganizationLayout;
